import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Users, RefreshCw, Compass, HeartHandshake } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import SponsorCard from "@/components/sponsors/SponsorCard";
import FilterBar from "@/components/sponsors/FilterBar";
import EmptyState from "@/components/common/EmptyState";
import { HEYCATCH_EVENTS, trackOutcome } from "@/lib/heycatch";
import RelatedLinks from "@/components/common/RelatedLinks";
import AvailabilityAlertForm from "@/components/sponsors/AvailabilityAlertForm";
import { SkeletonCard } from "@/components/common/Loading";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DEFAULTS = {
  q: "",
  program: "ALL",
  availability: "",
  communication: "",
  gender: "",
  state: "",
  country: "",
  online_only: false,
  page: 1,
};

const OTHER_PATHWAYS = [
  { key: "ALL", label: "All pathways" },
  { key: "AA", label: "AA" },
  { key: "NA", label: "NA" },
  { key: "SMART", label: "SMART Recovery" },
  { key: "CMA", label: "CMA" },
  { key: "OTHER", label: "Other pathway" },
];

export default function BrowseSponsors() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  /** Empty-state conversion: the one place we ask a visitor to fill the gap
   *  themselves. Tracked with a no-property event so we can see whether the
   *  honest empty state actually recruits sponsors. */
  const goBecomeSponsor = () => {
    trackOutcome(HEYCATCH_EVENTS.BECOME_SPONSOR_EMPTY_STATE_CLICKED);
    navigate("/become-a-sponsor");
  };
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    ...DEFAULTS,
    program: params.get("program") || "ALL",
  });
  const [data, setData] = useState({ sponsors: [], total: 0 });
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [stats, setStats] = useState(null);

  const load = useCallback(
    async ({ retry = true } = {}) => {
      setStatus((s) => (s === "ready" ? "loading" : s === "error" ? "loading" : s));
      try {
        const { data: res } = await api.get("/sponsors", {
          params: {
            q: filters.q || undefined,
            program: filters.program,
            availability: filters.availability || undefined,
            communication: filters.communication || undefined,
            gender: filters.gender || undefined,
            state: filters.state || undefined,
            country: filters.country || undefined,
            online_only: filters.online_only || undefined,
            page: filters.page,
            page_size: 12,
          },
          timeout: 20000,
        });
        setData(res);
        setStatus("ready");
      } catch (err) {
        // A transient failure on the first attempt is retried once; the error is
        // logged in development only and never swallowed silently.
        devWarn("BrowseSponsors.load", err);
        if (retry) {
          setTimeout(() => load({ retry: false }), 2500);
          return;
        }
        setStatus("error");
      }
    },
    [filters]
  );

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Aggregate counts, used only to suggest pathways that currently have sponsors.
  useEffect(() => {
    api
      .get("/sponsors/stats")
      .then(({ data: s }) => setStats(s))
      .catch(() => setStats(null));
  }, []);

  const toggleFavorite = async (sponsor) => {
    if (!user) {
      navigate("/register?next=/sponsors");
      return;
    }
    try {
      if (sponsor.is_favorite) {
        await api.delete(`/favorites/${sponsor.user_id}`);
      } else {
        await api.post(`/favorites/${sponsor.user_id}`);
      }
      setData((d) => ({
        ...d,
        sponsors: d.sponsors.map((s) =>
          s.user_id === sponsor.user_id ? { ...s, is_favorite: !s.is_favorite } : s
        ),
      }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startMessage = async (sponsor) => {
    if (!user) {
      navigate("/register?next=/sponsors");
      return;
    }
    try {
      const { data: convo } = await api.post("/conversations", { other_user_id: sponsor.user_id });
      navigate(`/messages?c=${convo.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / 12));
  const loading = status === "loading";
  const hasActiveFilters = Boolean(
    filters.q ||
      filters.availability ||
      filters.communication ||
      filters.gender ||
      filters.state ||
      filters.country ||
      filters.online_only
  );
  // Pathway-specific emptiness: a program is selected, no other filters narrowing it down.
  const isFellowshipEmpty = filters.program !== "ALL" && !hasActiveFilters;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="font-display text-3xl font-bold text-[#1F2A2E]">
        Browse Peer Recovery Sponsors
      </h1>
      {/* Definition first, in one quotable sentence (audit finding g_definition). */}
      <p data-testid="sponsors-definition" className="mt-3 text-[#1F2A2E] leading-7 max-w-3xl font-medium">
        A peer recovery sponsor is a person with relevant lived experience who volunteers to support
        another person in recovery.
      </p>
      <p className="mt-2 text-[#4B5A60] leading-7 max-w-3xl">
        Every profile here is written by a real member offering peer sponsorship. Browsing is free
        and needs no account. Take your time.
      </p>

      <div className="mt-6">
        <FilterBar filters={filters} setFilters={setFilters} total={data.total} loading={loading} />
      </div>

      <div className="mt-6">
        {status === "loading" ? (
          <div
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            data-testid="sponsors-loading"
            role="status"
            aria-live="polite"
          >
            <span className="sr-only">Loading sponsor profiles</span>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : status === "error" ? (
          <EmptyState
            icon={RefreshCw}
            testId="sponsors-error-state"
            title="We couldn't load the directory just now"
            description="This is a connection problem on our side, not something you did. Please try again in a moment - browsing is always free."
            action={
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => load({ retry: false })}
                  data-testid="sponsors-retry-btn"
                  className="h-11 touch-manipulation bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] text-white"
                >
                  Try again
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/resources")}
                  data-testid="sponsors-error-resources-btn"
                  className="h-11 touch-manipulation bg-white border-[#E3DED4]"
                >
                  Explore Recovery Resources
                </Button>
              </div>
            }
          />
        ) : data.sponsors.length === 0 ? (
          isFellowshipEmpty ? (
            <EmptyState
              icon={Compass}
              testId="sponsors-empty-pathway"
              title="No sponsors are available in this recovery pathway yet."
              description="Sponsor Me is a volunteer community, so availability changes as people join and update their profiles. You can look at every pathway, try another one, or offer to sponsor here yourself."
              action={
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Button
                      onClick={() => setFilters({ ...DEFAULTS, program: "ALL" })}
                      data-testid="empty-browse-all-btn"
                      className="h-11 touch-manipulation bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] text-white"
                    >
                      Browse all sponsors
                    </Button>
                    <Button
                      variant="outline"
                      onClick={goBecomeSponsor}
                      data-testid="empty-become-sponsor-btn"
                      className="h-11 touch-manipulation bg-white border-[#E3DED4]"
                    >
                      Become a Sponsor
                    </Button>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#7A868B]">
                      Choose another recovery pathway
                    </p>
                    <div
                      className="mt-2 flex flex-wrap gap-2 justify-center"
                      data-testid="empty-pathway-options"
                    >
                      {OTHER_PATHWAYS.filter((p) => p.key !== filters.program).map((p) => {
                        const count = stats?.programs?.[p.key]?.total;
                        return (
                          <button
                            key={p.key}
                            onClick={() => setFilters({ ...DEFAULTS, program: p.key })}
                            data-testid={`empty-pathway-${p.key}`}
                            className="min-h-[44px] rounded-xl border border-[#E3DED4] bg-white px-4 py-2 text-sm font-semibold text-[#1F6F78] transition-colors duration-150 touch-manipulation hover:bg-[#E9F3F3] active:bg-[#D7EEF0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1F6F78]"
                          >
                            {p.label}
                            {typeof count === "number" && count > 0 ? ` (${count})` : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Zero real results: offer to tell them when a sponsor turns
                      up. The form states plainly that alerts are not switched
                      on yet - see AvailabilityAlertForm. */}
                  <AvailabilityAlertForm program={filters.program} className="mt-2" />
                </div>
              }
            />
          ) : hasActiveFilters ? (
            <EmptyState
              icon={Users}
              testId="sponsors-empty-state"
              title="No sponsors match these filters yet"
              description="This community is still growing. Try clearing your filters, or consider becoming a sponsor yourself if you have recovery time to share."
              action={
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setFilters(DEFAULTS)}
                    data-testid="empty-clear-filters-btn"
                    className="h-11 touch-manipulation bg-white border-[#E3DED4]"
                  >
                    Clear filters
                  </Button>
                  <Button
                    onClick={goBecomeSponsor}
                    data-testid="empty-become-sponsor-btn"
                    className="h-11 touch-manipulation bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] text-white"
                  >
                    Become a Sponsor
                  </Button>
                  </div>
                  <AvailabilityAlertForm program={filters.program} />
                </div>
              }
            />
          ) : (
            <EmptyState
              icon={HeartHandshake}
              testId="sponsors-empty-community"
              title="We're building the Sponsor Me community."
              description="Sponsor Me is still growing, and some recovery pathways may not have an available sponsor yet. If you have recovery experience and want to give back, consider becoming one of the first sponsors in your community."
              action={
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Button
                      onClick={goBecomeSponsor}
                      data-testid="empty-become-sponsor-btn"
                      className="h-11 touch-manipulation bg-[#1F6F78] hover:bg-[#185B62] active:bg-[#134B52] text-white"
                    >
                      Become a Sponsor
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/about")}
                      data-testid="empty-how-it-works-btn"
                      className="h-11 touch-manipulation bg-white border-[#E3DED4]"
                    >
                      Learn How It Works
                    </Button>
                  </div>
                  <AvailabilityAlertForm program={filters.program} />
                </div>
              }
            />
          )
        ) : (
          <>
            <div data-testid="sponsor-grid" className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.sponsors.map((s) => (
                <SponsorCard
                  key={s.user_id}
                  sponsor={s}
                  onFavorite={toggleFavorite}
                  onMessage={startMessage}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  className="h-11 touch-manipulation bg-white border-[#E3DED4]"
                >
                  Previous
                </Button>
                <span className="text-sm text-[#4B5A60]">
                  Page {filters.page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={filters.page >= totalPages}
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  className="h-11 touch-manipulation bg-white border-[#E3DED4]"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      <RelatedLinks
        heading="Keep reading"
        links={[
          { to: "/become-a-sponsor", label: "Become a sponsor" },
          { to: "/resources", label: "Recovery resources and crisis lines" },
          { to: "/pricing", label: "Pricing for unlimited messaging" },
          { to: "/about", label: "About Sponsor Me" },
        ]}
      />
    </div>
  );
}
